import React from 'react';
import styled, { css, ThemeContext } from 'styled-components';
import { CSSTransition } from 'react-transition-group';
import Spacer from 'components/Spacer';
import { useAppDispatch } from 'store/hooks';
import { setVisibility } from 'store/features/ctaOverlay/ctaOverlaySlice';
import { setScrollingLocked } from 'store/features/utility/utilitySlice';
import ContactForm from 'components/ContactForm';
import Block from 'components/Block';
import PerfectScrollbar, { Props as PerfectScrollbarProps } from 'src/lib/components/PerfectScrollbar';
import useToBreakpoint from 'src/hooks/useToBreakpoint';
import TransitionElement from './TransitionElement';
import InfoSection from './InfoSection';
import Drawer from './Drawer';
import TransitionedCloseButton from './TransitionedCloseButton';
import { AllowedNumber, ShorthandMarginPadding } from '../Spacer/types';

export interface Props {
  visible: boolean,
  infoTitle?: string,
  infoSubTitle?: string,
  formTitle?: string,
  timeout?: number,
  onClose?: () => void,
}

const CtaOverlay: React.FC<Props> = ({
  visible,
  infoTitle = 'We\'re always ready to talk search marketing',
  infoSubTitle = 'You\'re in capable hands',
  formTitle = 'Tell us all about it',
  timeout = 300,
  onClose = () => {},
}) => {
  const theme = React.useContext(ThemeContext);
  const dispatch = useAppDispatch();
  const isMobile = useToBreakpoint('tablet');
  const handleCloseClick = () => {
    onClose();
    dispatch(setVisibility(false));
  };
  const lockScrolling = () => dispatch(setScrollingLocked(true));
  const unlockScrolling = () => dispatch(setScrollingLocked(false));
  const scrollbarStyleOverrides: PerfectScrollbarProps['styleOverrides'] = {
    scrollbarContainer: css`
      position: absolute !important;
      height: 100vh;
      width: 100%;
      margin: -10px;
      ${(p) => p.theme.breakpoints.up('tablet')} {
        display: flex;
      }
    `,
    thumbX: css`
      display: none;
    `,
  };

  const columnPadding: ShorthandMarginPadding | AllowedNumber = isMobile ? 2 : [2, 4];

  return (
    <CSSTransition
      in={visible}
      timeout={timeout}
      mountOnEnter
      unmountOnExit
      onEntered={lockScrolling}
      onExited={unlockScrolling}
    >
      <TransitionElement>
        <FixedElement>
          <PerfectScrollbarStyled
            styleOverrides={scrollbarStyleOverrides}
            color={theme.palette.interactive}
          >
            <TransitionedCloseButton
              timeout={timeout}
              onClick={handleCloseClick}
            />

            <DrawerWrap>
              <Drawer timeout={timeout}>
                <BlockFlexed>
                  <Inner p={columnPadding}>
                    <InfoSection
                      title={infoTitle}
                      subTitle={infoSubTitle}
                    />
                  </Inner>
                </BlockFlexed>
              </Drawer>

              <Drawer timeout={timeout} flipped>
                <BlockFlexed darkBg type="noPadding">
                  <Inner p={columnPadding}>
                    <ContactFormWrap>
                      <Spacer mt={5}>
                        <ContactForm formTitle={formTitle} />
                      </Spacer>
                    </ContactFormWrap>
                  </Inner>
                </BlockFlexed>
              </Drawer>
            </DrawerWrap>

          </PerfectScrollbarStyled>
        </FixedElement>
      </TransitionElement>
    </CSSTransition>
  );
};
export default CtaOverlay;


const FixedElement = styled.div`
  position: fixed;
  display: flex;
  flex-direction: column;
  flex: 1 0 auto;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
  z-index: 3;
`;
const PerfectScrollbarStyled = styled(PerfectScrollbar)`
  box-sizing: border-box;
  padding: 10px;
`;
const DrawerWrap = styled.div`
  position: absolute;
  width: 100%;
  min-height: 100%;
  display: flex;
  flex-direction: column;
  flex: 1 0 auto;
  ${({ theme }) => theme.breakpoints.up('tablet')} {
    flex-direction: row;
  }
`;
const Inner = styled(Spacer)`
  position: relative;
`;
const BlockFlexed = styled(Block)`
  background-color: #fff;
  flex: 1 0 auto;
  @media screen and (min-height: 50em) {
    display: flex;
    flex-direction: column;
    justify-content: center;
  }
`;
const ContactFormWrap = styled.div`
  display: flex;
  justify-content: flex-end;  
`;
